import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Alert, Spinner, Badge, Modal, Table } from 'react-bootstrap';
import {
  FaPencilAlt, FaTimes, FaPhoneAlt, FaEnvelope, FaLandmark,
  FaMapMarkerAlt, FaBuilding, FaImage, FaLock, FaKey,
  FaUsers, FaUserPlus, FaTrash
} from 'react-icons/fa';
import { MdOutlineFileUpload } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import 'react-phone-number-input/style.css'; 

import Wrapper from '../../components/Wrapper';
import axiosInstance from '../../utils/axios';
import '../../styles/profile.css';
import { getRole } from '../../utils/role';
import PartnerDisclaimer from '../../components/Forms/PartnerDisclaimer';
import { getPartnerLogoPresentation, usePartnerLogoAspect } from '../../utils/partnerLogoDisplay';

function PartnerProfile() {
  const { id } = useParams();

  const role = getRole();
  const isSupport = role === 'SUPPORT';
  const isPartner = role === 'PARTNER';
  const isTeamMember = role === 'TEAM_MEMBER';

  const targetAdminId = id;

  const { partner: reduxPartner } = useSelector((state) => state.partner);

  // --- CORE STATE ---
  const [partnerData, setPartnerData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [editInfo, setEditInfo] = useState(false);

  // --- UPLOAD STATES ---
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showUploader, setShowUploader] = useState(false);
  
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState({ type: '', text: '' });
  const [showBannerUploader, setShowBannerUploader] = useState(false);
  
  const [selectedLandscapeBanner, setSelectedLandscapeBanner] = useState(null);
  const [landscapeBannerPreview, setLandscapeBannerPreview] = useState('');
  const [uploadingLandscapeBanner, setUploadingLandscapeBanner] = useState(false);
  const [landscapeBannerMessage, setLandscapeBannerMessage] = useState({ type: '', text: '' });
  const [showLandscapeBannerUploader, setShowLandscapeBannerUploader] = useState(false);
  
  const [selectedWatermark, setSelectedWatermark] = useState(null);
  const [watermarkPreview, setWatermarkPreview] = useState('');
  const [uploadingWatermark, setUploadingWatermark] = useState(false);
  const [watermarkMessage, setWatermarkMessage] = useState({ type: '', text: '' });
  const [showWatermarkUploader, setShowWatermarkUploader] = useState(false);

  // --- EDIT FORM STATES ---
  const [formData, setFormData] = useState({
    entity_name: '',
    primary_person_name: '',
    primary_person_mobile_no: '',
    primary_person_email_id: '',
    business_address: '',
    city: '',
    state: '',
    pincode: '',
    password: '',
    logo_position: 'left',
    title_position: 'left' 
  });
  const [updating, setUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState({ type: '', text: '' });

  // --- TEAM MEMBER STATES ---
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [newMemberData, setNewMemberData] = useState({ member_name: '', email_id: '', password: '' });
  const [addingMember, setAddingMember] = useState(false);
  const [memberMessage, setMemberMessage] = useState({ type: '', text: '' });

  // --- EFFECTS ---
  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      setUpdateMessage({ type: '', text: '' });

      try {
        let endpoint = '';
        if (isPartner || isTeamMember) {
          endpoint = '/admin/info';
        } else if (isSupport && targetAdminId) {
          endpoint = `/partner/info/${targetAdminId}`;
        }

        const res = await axiosInstance.get(endpoint);
        if (res.data) {
          setPartnerData(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch profile info", err);
        setUpdateMessage({ type: 'danger', text: 'Failed to fetch user data.' });
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [isPartner, isTeamMember, isSupport, targetAdminId]);

  useEffect(() => {
    if (partnerData && editInfo) {
      setFormData({
        entity_name: partnerData.entity_name || '',
        primary_person_name: partnerData.primary_person_name || '',
        primary_person_mobile_no: partnerData.primary_person_mobile_no || '',
        primary_person_email_id: partnerData.primary_person_email_id || '',
        business_address: partnerData.business_address || '',
        city: partnerData.city || '',
        state: partnerData.state || '',
        pincode: partnerData.pincode || '',
        password: '',
        logo_position: partnerData.logo_position || 'left',
        title_position: partnerData.title_position || 'left'
      });
    }
  }, [partnerData, editInfo]);

  // --- PROFILE HANDLERS ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    if (formData.primary_person_mobile_no && !isValidPhoneNumber(formData.primary_person_mobile_no)) {
      setUpdateMessage({ type: 'danger', text: 'Please enter a valid international mobile number.' });
      return;
    }

    if (formData.pincode && !/^[a-zA-Z0-9\s\-]{3,12}$/.test(formData.pincode)) {
      setUpdateMessage({ type: 'danger', text: 'Please enter a valid Zip/Postal Code (3-12 characters, alphanumeric).' });
      return;
    }

    setUpdating(true);
    setUpdateMessage({ type: '', text: '' });

    try {
      const payload = { ...formData };
      if (!payload.password) delete payload.password;

      let endpoint = '';
      if (isPartner || isTeamMember) {
        endpoint = `/admin/profile`;
      } else if (isSupport && targetAdminId) {
        endpoint = `/partner/profile/${targetAdminId}`;
      }

      const res = await axiosInstance.put(endpoint, payload);

      if (res.data.success) {
        setUpdateMessage({ type: 'success', text: 'Profile updated successfully! 🎉' });
        setPartnerData(prev => ({ ...prev, ...formData }));

        if (isPartner || isTeamMember) {
          const updatedPartner = { ...reduxPartner, ...formData };
          localStorage.setItem('partner', JSON.stringify(updatedPartner));
          setTimeout(() => window.location.reload(), 1500);
        } else {
           setTimeout(() => setEditInfo(false), 1500);
        }
      }
    } catch (err) {
      setUpdateMessage({ type: 'danger', text: err.response?.data?.msg || 'Failed to update profile.' });
    } finally {
      setUpdating(false);
    }
  };

  // --- TEAM MEMBER HANDLERS ---
  const handleAddMember = async (e) => {
    e.preventDefault();
    setAddingMember(true);
    setMemberMessage({ type: '', text: '' });

    try {
      let endpoint = '/admin/team-member';
      if (isSupport && targetAdminId) {
        endpoint = `/super-admin/team-member/${targetAdminId}`;
      }

      const res = await axiosInstance.post(endpoint, newMemberData);
      
      if (res.data.success) {
        setMemberMessage({ type: 'success', text: 'Team member added successfully!' });
        setPartnerData(prev => ({
          ...prev,
          team_members: [...(prev.team_members || []), res.data.data]
        }));
        setTimeout(() => {
          setShowAddMemberModal(false);
          setNewMemberData({ member_name: '', email_id: '', password: '' });
          setMemberMessage({ type: '', text: '' });
        }, 1500);
      }
    } catch (err) {
      setMemberMessage({ type: 'danger', text: err.response?.data?.msg || 'Failed to add team member.' });
    } finally {
      setAddingMember(false);
    }
  };

  const handleDeleteMember = async (member_id) => {
    if (!window.confirm("Are you sure you want to delete this team member? Their access will be revoked immediately.")) return;
    try {
      let endpoint = `/admin/team-member/${member_id}`;
      if (isSupport && targetAdminId) {
        endpoint = `/super-admin/team-member/${targetAdminId}/${member_id}`;
      }

      const res = await axiosInstance.delete(endpoint);
      
      if (res.data.success) {
        setPartnerData(prev => ({
          ...prev,
          team_members: prev.team_members.filter(m => m.member_id !== member_id)
        }));
      }
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to delete team member");
    }
  };

  // --- UPLOAD HANDLERS ---
  const effectiveAdminId = targetAdminId || partnerData?.admin_id || reduxPartner?.admin_id;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'danger', text: 'Only image files are allowed' });
      return;
    }
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
    setMessage({ type: '', text: '' });
  };

  const handleUpload = async () => {
    if (!selectedFile) return setMessage({ type: 'danger', text: 'Please select an image first' });
    const uploadData = new FormData();
    uploadData.append('logo', selectedFile);
    setUploading(true);
    try {
      const res = await axiosInstance.post(`/partner/upload-logo/${effectiveAdminId}`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setMessage({ type: 'success', text: 'Logo updated successfully 🎉' });
        setPartnerData(prev => ({ ...prev, profile_picture: res.data.profile_picture }));
        if (isPartner || isTeamMember) {
          localStorage.setItem('partner', JSON.stringify({ ...reduxPartner, profile_picture: res.data.profile_picture }));
          setTimeout(() => window.location.reload(), 1500);
        } else {
          setTimeout(() => { setShowUploader(false); setSelectedFile(null); setPreview(''); }, 1500);
        }
      }
    } catch (err) {
      setMessage({ type: 'danger', text: err.response?.data?.msg || 'Upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  const handleBannerFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return setBannerMessage({ type: 'danger', text: 'Only image files are allowed' });
    setSelectedBanner(file);
    setBannerPreview(URL.createObjectURL(file));
    setBannerMessage({ type: '', text: '' });
  };

  const handleBannerUpload = async () => {
    if (!selectedBanner) return setBannerMessage({ type: 'danger', text: 'Please select an image first' });
    const uploadData = new FormData();
    uploadData.append('banner', selectedBanner);
    setUploadingBanner(true);
    try {
      const res = await axiosInstance.post(`/partner/upload-banner/${effectiveAdminId}`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setBannerMessage({ type: 'success', text: 'Banner updated successfully 🎉' });
        setPartnerData(prev => ({ ...prev, banner_picture: res.data.banner_picture }));
        if (isPartner || isTeamMember) {
          localStorage.setItem('partner', JSON.stringify({ ...reduxPartner, banner_picture: res.data.banner_picture }));
          setTimeout(() => window.location.reload(), 1500);
        } else {
          setTimeout(() => { setShowBannerUploader(false); setSelectedBanner(null); setBannerPreview(''); }, 1500);
        }
      }
    } catch (err) {
      setBannerMessage({ type: 'danger', text: err.response?.data?.msg || 'Upload failed.' });
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleLandscapeBannerFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return setLandscapeBannerMessage({ type: 'danger', text: 'Only image files are allowed' });
    setSelectedLandscapeBanner(file);
    setLandscapeBannerPreview(URL.createObjectURL(file));
    setLandscapeBannerMessage({ type: '', text: '' });
  };

  const handleLandscapeBannerUpload = async () => {
    if (!selectedLandscapeBanner) return setLandscapeBannerMessage({ type: 'danger', text: 'Please select an image first' });
    const uploadData = new FormData();
    uploadData.append('landscape_banner', selectedLandscapeBanner);
    setUploadingLandscapeBanner(true);
    try {
      const res = await axiosInstance.post(`/partner/upload-landscape-banner/${effectiveAdminId}`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setLandscapeBannerMessage({ type: 'success', text: 'Landscape Banner updated successfully 🎉' });
        setPartnerData(prev => ({ ...prev, landscape_banner_picture: res.data.landscape_banner_picture }));
        if (isPartner || isTeamMember) {
          localStorage.setItem('partner', JSON.stringify({ ...reduxPartner, landscape_banner_picture: res.data.landscape_banner_picture }));
          setTimeout(() => window.location.reload(), 1500);
        } else {
          setTimeout(() => { setShowLandscapeBannerUploader(false); setSelectedLandscapeBanner(null); setLandscapeBannerPreview(''); }, 1500);
        }
      }
    } catch (err) {
      setLandscapeBannerMessage({ type: 'danger', text: err.response?.data?.msg || 'Upload failed.' });
    } finally {
      setUploadingLandscapeBanner(false);
    }
  };

  const handleWatermarkFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return setWatermarkMessage({ type: 'danger', text: 'Only image files are allowed' });
    setSelectedWatermark(file);
    setWatermarkPreview(URL.createObjectURL(file));
    setWatermarkMessage({ type: '', text: '' });
  };

  const handleWatermarkUpload = async () => {
    if (!selectedWatermark) return setWatermarkMessage({ type: 'danger', text: 'Please select an image first' });
    const uploadData = new FormData();
    uploadData.append('watermark', selectedWatermark);
    setUploadingWatermark(true);
    try {
      const res = await axiosInstance.post(`/partner/upload-watermark/${effectiveAdminId}`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setWatermarkMessage({ type: 'success', text: 'Watermark updated successfully 🎉' });
        setPartnerData(prev => ({ ...prev, watermark_picture: res.data.watermark_picture }));
        if (isPartner || isTeamMember) {
          localStorage.setItem('partner', JSON.stringify({ ...reduxPartner, watermark_picture: res.data.watermark_picture }));
          setTimeout(() => window.location.reload(), 1500);
        } else {
          setTimeout(() => { setShowWatermarkUploader(false); setSelectedWatermark(null); setWatermarkPreview(''); }, 1500);
        }
      }
    } catch (err) {
      setWatermarkMessage({ type: 'danger', text: err.response?.data?.msg || 'Upload failed.' });
    } finally {
      setUploadingWatermark(false);
    }
  };

  // --- PERMISSION CHECKS & UI HELPERS ---
  
  const isEntityLocked = isPartner || isTeamMember; 
  const isLogoLocked = isPartner || isTeamMember;
  const isLayoutLocked = isPartner || isTeamMember;
  
  const isContactLocked = false; 
  const isOrgAddressLocked = isTeamMember;

  const { isBanner: profileLogoIsBanner, onImageLoad: onProfileLogoLoad } = usePartnerLogoAspect(partnerData?.profile_picture);
  const profileLogoPresentation = getPartnerLogoPresentation(profileLogoIsBanner, 'profile');

  if (loadingData) {
    return <Wrapper><div className="text-center p-5"><Spinner animation="border" /></div></Wrapper>;
  }

  const currentMembersCount = partnerData?.team_members?.length || 0;
  const hasReachedLimit = currentMembersCount >= 5;

  return (
    <Wrapper>
      <div style={{ overflow: 'hidden', width: '100%', maxWidth: '100vw' }}>
        <Row className="g-4 m-0">
          
          {/* ========================================= */}
          {/* LEFT COLUMN: PROFILE CARD                 */}
          {/* ========================================= */}
          <Col md={6} className="px-2">
            <Card className="rounded-4 shadow-sm h-100 border-0">
              <Card.Body className="p-0">
                
                {/* HEADER */}
                <div className="d-flex align-items-center gap-3 p-4 border-bottom">
                  <div
                    className={profileLogoPresentation.containerClassName}
                    style={{
                      ...profileLogoPresentation.containerStyle,
                      cursor: isLogoLocked ? 'default' : 'pointer',
                    }}
                    onClick={() => !isLogoLocked && setShowUploader(true)}
                  >
                    {partnerData?.profile_picture ? (
                      <img
                        src={partnerData.profile_picture}
                        alt="Logo"
                        className={profileLogoPresentation.imgClassName}
                        style={profileLogoPresentation.imgStyle}
                        onLoad={onProfileLogoLoad}
                      />
                    ) : (
                      <span style={{ fontSize: 40, fontWeight: 'bold' }}>
                        {partnerData?.entity_name?.[0] || 'P'}
                      </span>
                    )}
                    {!isLogoLocked && (
                      <div className="position-absolute bottom-0 end-0 bg-white rounded-circle p-1 shadow">
                        <MdOutlineFileUpload className="text-primary" size={18} />
                      </div>
                    )}
                  </div>
                  <div className="flex-grow-1 overflow-hidden">
                    <h4 className="fw-bold mb-1 text-truncate">{partnerData?.entity_name}</h4>
                    <p className="text-muted mb-0">{isSupport ? 'Partner Details' : 'Partner Account'}</p>
                  </div>
                </div>

                {/* INFO / EDIT SECTION */}
                <div className="p-4 bg-light position-relative rounded-bottom-4">
                  <div className="position-absolute top-0 end-0 m-3 cursor-pointer">
                    {editInfo ? (
                      <FaTimes onClick={() => {
                        setEditInfo(false);
                        setUpdateMessage({ type: '', text: '' });
                      }} />
                    ) : (
                      <FaPencilAlt onClick={() => setEditInfo(true)} />
                    )}
                  </div>

                  {updateMessage.text && (
                    <Alert variant={updateMessage.type} dismissible onClose={() => setUpdateMessage({ type: '', text: '' })} className="mb-3">
                      {updateMessage.text}
                    </Alert>
                  )}

                  {editInfo ? (
                    <Form onSubmit={handleUpdateProfile}>
                      {/* Organization Name */}
                      <Form.Group className="mb-3">
                        <Form.Label className="d-flex align-items-center gap-2 text-secondary">
                          <FaBuilding /> Organization Name
                          {isEntityLocked && (
                            <Badge bg="warning" text="dark" className="ms-2">
                              <FaLock size={10} className="me-1" /> Locked
                            </Badge>
                          )}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="entity_name"
                          value={formData.entity_name}
                          onChange={handleInputChange}
                          disabled={isEntityLocked}
                          required
                          style={isEntityLocked ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                        />
                        {isEntityLocked && (
                          <Form.Text className="text-muted">
                            <FaLock size={10} className="me-1" />
                            Only superadmin can change organization name. Contact support if needed.
                          </Form.Text>
                        )}
                      </Form.Group>

                      {/* PDF Logo & Title Position */}
                      <Row className="mb-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="d-flex align-items-center gap-2 text-secondary">
                               Logo Position (PDF)
                              {isLayoutLocked && <Badge bg="secondary" className="ms-2"><FaLock size={8} /></Badge>}
                            </Form.Label>
                            <Form.Select
                              name="logo_position"
                              value={formData.logo_position}
                              onChange={handleInputChange}
                              disabled={isLayoutLocked}
                              style={isLayoutLocked ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                            >
                              <option value="left">Left</option>
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="d-flex align-items-center gap-2 text-secondary">
                               Title Position (PDF)
                              {isLayoutLocked && <Badge bg="secondary" className="ms-2"><FaLock size={8} /></Badge>}
                            </Form.Label>
                            <Form.Select
                              name="title_position"
                              value={formData.title_position}
                              onChange={handleInputChange}
                              disabled={isLayoutLocked}
                              style={isLayoutLocked ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                            >
                              <option value="left">Left</option>
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>

                      {(isSupport || isTeamMember) && (
                        <Form.Group className="mb-3">
                          <Form.Label className="d-flex align-items-center gap-2 text-secondary">
                              <FaKey /> Set New Password
                          </Form.Label>
                          <Form.Control
                              type="password"
                              name="password"
                              value={formData.password}
                              onChange={handleInputChange}
                              placeholder="Leave empty to keep current password"
                              autoComplete="new-password"
                          />
                          <Form.Text className="text-muted">
                              Optional. Enter value only if you want to change it.
                          </Form.Text>
                        </Form.Group>
                      )}

                      {/* Contact Fields (Name & Email editable by everyone) */}
                      <Form.Group className="mb-3">
                        <Form.Label className="d-flex align-items-center gap-2 text-secondary">
                          <FaPhoneAlt /> Your Name
                          {isContactLocked && <Badge bg="secondary" className="ms-2"><FaLock size={8} /></Badge>}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="primary_person_name"
                          value={formData.primary_person_name}
                          onChange={handleInputChange}
                          required
                          disabled={isContactLocked}
                          style={isContactLocked ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label className="d-flex align-items-center gap-2 text-secondary">
                          <FaEnvelope /> Your Email Address
                          {isContactLocked && <Badge bg="secondary" className="ms-2"><FaLock size={8} /></Badge>}
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="primary_person_email_id"
                          value={formData.primary_person_email_id}
                          onChange={handleInputChange}
                          required
                          disabled={isContactLocked}
                          style={isContactLocked ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                        />
                      </Form.Group>

                      {/* Organization Specific Fields (Locked for Team Members) */}
                      <Form.Group className="mb-3">
                        <Form.Label className="d-flex align-items-center gap-2 text-secondary">
                          <FaPhoneAlt /> Organization Mobile
                          {isOrgAddressLocked && <Badge bg="secondary" className="ms-2"><FaLock size={8} /></Badge>}
                        </Form.Label>
                        <PhoneInput
                          international
                          defaultCountry="IN"
                          value={formData.primary_person_mobile_no}
                          onChange={(val) => setFormData(prev => ({ ...prev, primary_person_mobile_no: val }))}
                          disabled={isOrgAddressLocked}
                          className={`form-control d-flex align-items-center ${isOrgAddressLocked ? 'bg-light' : ''}`}
                          style={isOrgAddressLocked ? { cursor: 'not-allowed' } : {}}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label className="d-flex align-items-center gap-2 text-secondary">
                          <FaLandmark /> Business Address
                          {isOrgAddressLocked && <Badge bg="secondary" className="ms-2"><FaLock size={8} /></Badge>}
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="business_address"
                          value={formData.business_address}
                          onChange={handleInputChange}
                          required
                          disabled={isOrgAddressLocked}
                          style={isOrgAddressLocked ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                        />
                      </Form.Group>

                      <Row className="mb-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="d-flex align-items-center gap-2 text-secondary">
                              <FaMapMarkerAlt /> City
                              {isOrgAddressLocked && <Badge bg="secondary" className="ms-2"><FaLock size={8} /></Badge>}
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="city"
                              value={formData.city}
                              onChange={handleInputChange}
                              required
                              disabled={isOrgAddressLocked}
                              style={isOrgAddressLocked ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="d-flex align-items-center gap-2 text-secondary">
                              <FaMapMarkerAlt /> State
                              {isOrgAddressLocked && <Badge bg="secondary" className="ms-2"><FaLock size={8} /></Badge>}
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="state"
                              value={formData.state}
                              onChange={handleInputChange}
                              required
                              disabled={isOrgAddressLocked}
                              style={isOrgAddressLocked ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-3">
                        <Form.Label className="d-flex align-items-center gap-2 text-secondary">
                          <FaMapMarkerAlt /> Zip / Postal Code
                          {isOrgAddressLocked && <Badge bg="secondary" className="ms-2"><FaLock size={8} /></Badge>}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleInputChange}
                          pattern="[a-zA-Z0-9\s\-]{3,12}"
                          maxLength="12"
                          required
                          disabled={isOrgAddressLocked}
                          style={isOrgAddressLocked ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                        />
                      </Form.Group>

                      <div className="d-flex gap-2">
                        <Button type="submit" variant="primary" disabled={updating} className="flex-grow-1">
                          {updating ? <><Spinner size="sm" className="me-2" />Updating...</> : 'Save Changes'}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => {
                            setEditInfo(false);
                            setUpdateMessage({ type: '', text: '' });
                          }}
                          disabled={updating}
                        >
                          Cancel
                        </Button>
                      </div>
                    </Form>
                  ) : (
                    <>
                      <Row className="mb-3 g-2 mx-0">
                        <Col xs="auto" className="text-secondary"><FaBuilding /></Col>
                        <Col className="text-break pe-4">{partnerData?.entity_name}</Col>
                      </Row>
                      
                      <Row className="mb-3 g-2 mx-0">
                         <Col xs="auto" className="text-secondary"><FaPhoneAlt /></Col>
                         <Col className="text-break pe-4">{partnerData?.primary_person_mobile_no}</Col>
                      </Row>

                       <Row className="mb-3 g-2 mx-0">
                        <Col xs="auto" className="text-secondary"><FaEnvelope /></Col>
                        <Col className="text-break pe-4">{partnerData?.primary_person_email_id}</Col>
                      </Row>
                      <Row className="mb-3 g-2 mx-0">
                        <Col xs="auto" className="text-secondary"><FaLandmark /></Col>
                        <Col className="text-break pe-4">{partnerData?.business_address}</Col>
                      </Row>
                      <Row className="g-2 mx-0">
                        <Col xs="auto" className="text-secondary"><FaMapMarkerAlt /></Col>
                        <Col className="text-break pe-4">{partnerData?.city}, {partnerData?.state} {partnerData?.pincode}</Col>
                      </Row>
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* ========================================= */}
          {/* RIGHT COLUMN: UPLOADERS                   */}
          {/* ========================================= */}
          <Col md={6} className="px-2">
            
            {/* LOGO UPLOADER */}
            <Card className="rounded-4 shadow-sm border-0 mb-4">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <FaImage /> Organization Logo
                    {isLogoLocked && (
                      <Badge bg="warning" text="dark"><FaLock size={10} className="me-1" /> Locked</Badge>
                    )}
                  </h5>
                  {!isLogoLocked && (
                    <Button size="sm" onClick={() => setShowUploader(!showUploader)}>
                      {showUploader ? 'Cancel' : 'Change Logo'}
                    </Button>
                  )}
                </div>

                {isLogoLocked && (
                  <Alert variant="info" className="mb-3">
                    <FaLock size={12} className="me-2" />
                    Only superadmin can change the organization logo. Contact support if you need to update it.
                  </Alert>
                )}

                 {message.text && (
                  <Alert variant={message.type} dismissible onClose={() => setMessage({ type: '', text: '' })}>
                    {message.text}
                  </Alert>
                )}

                {isLogoLocked ? (
                  <div className="text-center py-4" style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                     {partnerData?.profile_picture ? (
                       <img src={partnerData.profile_picture} alt="Current Logo" style={{ maxHeight: '200px', maxWidth: '100%', objectFit: 'contain' }} className="border rounded p-3" />
                     ) : (
                       <div className="text-muted"><FaImage size={48} className="mb-2 d-block mx-auto opacity-50" /><p>No logo uploaded yet</p></div>
                     )}
                  </div>
                ) : (
                  <>
                    {showUploader ? (
                        <>
                           <label htmlFor="logoUpload" className="border border-2 border-dashed rounded-4 p-4 text-center d-block" style={{ cursor: 'pointer', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                             {preview ? <img src={preview} alt="Preview" style={{ maxHeight: '180px', maxWidth: '100%', objectFit: 'contain' }} /> : 
                             <div><FaImage size={40} className="text-muted mb-2 d-block mx-auto" /><p className="text-muted mb-1">Click to upload logo</p></div>}
                           </label>
                           <input type="file" id="logoUpload" hidden accept="image/*" onChange={handleFileChange} />
                           <Button className="mt-3 w-100" onClick={handleUpload} disabled={uploading || !selectedFile}>
                              {uploading ? <><Spinner size="sm" className="me-2" />Uploading...</> : 'Upload Logo'}
                           </Button>
                        </>
                    ) : (
                       <div className="text-center py-4" style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {partnerData?.profile_picture ? (
                           <img src={partnerData.profile_picture} alt="Current Logo" style={{ maxHeight: '200px', maxWidth: '100%', objectFit: 'contain' }} className="border rounded p-3" />
                        ) : (
                           <div className="text-muted"><FaImage size={48} className="mb-2 d-block mx-auto opacity-50" /><p>No logo uploaded yet</p></div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>

            {/* FULL HEADER BANNER */}
            <Card className="rounded-4 shadow-sm border-0 mb-4">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <FaImage /> Full Header Banner
                    {isLogoLocked && (
                      <Badge bg="warning" text="dark"><FaLock size={10} className="me-1" /> Locked</Badge>
                    )}
                  </h5>
                  {!isLogoLocked && (
                    <Button size="sm" onClick={() => setShowBannerUploader(!showBannerUploader)}>
                      {showBannerUploader ? 'Cancel' : 'Change Banner'}
                    </Button>
                  )}
                </div>

                <p className="text-muted small mb-3">
                  Upload a full-width image (approx. 1524px x 150px) to completely override the standard PDF header layout.
                </p>

                {bannerMessage.text && (
                  <Alert variant={bannerMessage.type} dismissible onClose={() => setBannerMessage({ type: '', text: '' })}>
                    {bannerMessage.text}
                  </Alert>
                )}

                {isLogoLocked ? (
                  <div className="text-center py-4 border rounded bg-light" style={{ minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                     {partnerData?.banner_picture ? (
                       <img src={partnerData.banner_picture} alt="Banner" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} />
                     ) : (
                       <div className="text-muted"><FaImage size={32} className="mb-2 d-block mx-auto opacity-50" /><p className="mb-0">No banner uploaded</p></div>
                     )}
                  </div>
                ) : (
                  <>
                    {showBannerUploader ? (
                        <>
                           <label htmlFor="bannerUpload" className="border border-2 border-dashed rounded-4 p-3 text-center d-block bg-light" style={{ cursor: 'pointer', minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                             {bannerPreview ? <img src={bannerPreview} alt="Preview" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} /> : 
                             <div><FaImage size={32} className="text-muted mb-2 d-block mx-auto" /><p className="text-muted mb-1">Click to upload banner</p></div>}
                           </label>
                           <input type="file" id="bannerUpload" hidden accept="image/*" onChange={handleBannerFileChange} />
                           <Button className="mt-3 w-100" onClick={handleBannerUpload} disabled={uploadingBanner || !selectedBanner}>
                              {uploadingBanner ? <><Spinner size="sm" className="me-2" />Uploading...</> : 'Upload Banner'}
                           </Button>
                        </>
                    ) : (
                       <div className="text-center py-4 border rounded bg-light" style={{ minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {partnerData?.banner_picture ? (
                           <img src={partnerData.banner_picture} alt="Current Banner" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} />
                        ) : (
                           <div className="text-muted"><FaImage size={32} className="mb-2 d-block mx-auto opacity-50" /><p className="mb-0">No banner uploaded</p></div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>

            {/* LANDSCAPE HEADER BANNER */}
            <Card className="rounded-4 shadow-sm border-0 mb-4">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <FaImage /> Landscape Header Banner
                    {isLogoLocked && (
                      <Badge bg="warning" text="dark"><FaLock size={10} className="me-1" /> Locked</Badge>
                    )}
                  </h5>
                  {!isLogoLocked && (
                    <Button size="sm" onClick={() => setShowLandscapeBannerUploader(!showLandscapeBannerUploader)}>
                      {showLandscapeBannerUploader ? 'Cancel' : 'Change Banner'}
                    </Button>
                  )}
                </div>

                <p className="text-muted small mb-3">
                  Upload a wider full-width image (<strong>2182px x 150px</strong>) specifically for 1-Pager Landscape Reports.
                </p>

                {landscapeBannerMessage.text && (
                  <Alert variant={landscapeBannerMessage.type} dismissible onClose={() => setLandscapeBannerMessage({ type: '', text: '' })}>
                    {landscapeBannerMessage.text}
                  </Alert>
                )}

                {isLogoLocked ? (
                  <div className="text-center py-4 border rounded bg-light" style={{ minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                     {partnerData?.landscape_banner_picture ? (
                       <img src={partnerData.landscape_banner_picture} alt="Landscape Banner" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} />
                     ) : (
                       <div className="text-muted"><FaImage size={32} className="mb-2 d-block mx-auto opacity-50" /><p className="mb-0">No landscape banner uploaded</p></div>
                     )}
                  </div>
                ) : (
                  <>
                    {showLandscapeBannerUploader ? (
                        <>
                           <label htmlFor="landscapeBannerUpload" className="border border-2 border-dashed rounded-4 p-3 text-center d-block bg-light" style={{ cursor: 'pointer', minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                             {landscapeBannerPreview ? <img src={landscapeBannerPreview} alt="Preview" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} /> : 
                             <div><FaImage size={32} className="text-muted mb-2 d-block mx-auto" /><p className="text-muted mb-1">Click to upload landscape banner</p></div>}
                           </label>
                           <input type="file" id="landscapeBannerUpload" hidden accept="image/*" onChange={handleLandscapeBannerFileChange} />
                           <Button className="mt-3 w-100" onClick={handleLandscapeBannerUpload} disabled={uploadingLandscapeBanner || !selectedLandscapeBanner}>
                              {uploadingLandscapeBanner ? <><Spinner size="sm" className="me-2" />Uploading...</> : 'Upload Banner'}
                           </Button>
                        </>
                    ) : (
                       <div className="text-center py-4 border rounded bg-light" style={{ minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {partnerData?.landscape_banner_picture ? (
                           <img src={partnerData.landscape_banner_picture} alt="Current Landscape Banner" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} />
                        ) : (
                           <div className="text-muted"><FaImage size={32} className="mb-2 d-block mx-auto opacity-50" /><p className="mb-0">No landscape banner uploaded</p></div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>

            {/* WATERMARK */}
            <Card className="rounded-4 shadow-sm border-0">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <FaImage /> Background Watermark
                    {isLogoLocked && (
                      <Badge bg="warning" text="dark"><FaLock size={10} className="me-1" /> Locked</Badge>
                    )}
                  </h5>
                  {!isLogoLocked && (
                    <Button size="sm" onClick={() => setShowWatermarkUploader(!showWatermarkUploader)}>
                      {showWatermarkUploader ? 'Cancel' : 'Change Watermark'}
                    </Button>
                  )}
                </div>

                <p className="text-muted small mb-3">
                  Upload an image to appear as a faded background watermark centered on the PDF pages. <br/>
                  <strong>Recommended size:</strong> 800x800px (Square).
                </p>

                {watermarkMessage.text && (
                  <Alert variant={watermarkMessage.type} dismissible onClose={() => setWatermarkMessage({ type: '', text: '' })}>
                    {watermarkMessage.text}
                  </Alert>
                )}

                {isLogoLocked ? (
                  <div className="text-center py-4 border rounded bg-light" style={{ minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                     {partnerData?.watermark_picture ? (
                       <img src={partnerData.watermark_picture} alt="Watermark" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} />
                     ) : (
                       <div className="text-muted"><FaImage size={32} className="mb-2 d-block mx-auto opacity-50" /><p className="mb-0">No watermark uploaded</p></div>
                     )}
                  </div>
                ) : (
                  <>
                    {showWatermarkUploader ? (
                        <>
                           <label htmlFor="watermarkUpload" className="border border-2 border-dashed rounded-4 p-3 text-center d-block bg-light" style={{ cursor: 'pointer', minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                             {watermarkPreview ? <img src={watermarkPreview} alt="Preview" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} /> : 
                             <div><FaImage size={32} className="text-muted mb-2 d-block mx-auto" /><p className="text-muted mb-1">Click to upload watermark</p></div>}
                           </label>
                           <input type="file" id="watermarkUpload" hidden accept="image/*" onChange={handleWatermarkFileChange} />
                           <Button className="mt-3 w-100" onClick={handleWatermarkUpload} disabled={uploadingWatermark || !selectedWatermark}>
                              {uploadingWatermark ? <><Spinner size="sm" className="me-2" />Uploading...</> : 'Upload Watermark'}
                           </Button>
                        </>
                    ) : (
                       <div className="text-center py-4 border rounded bg-light" style={{ minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {partnerData?.watermark_picture ? (
                           <img src={partnerData.watermark_picture} alt="Current Watermark" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} />
                        ) : (
                           <div className="text-muted"><FaImage size={32} className="mb-2 d-block mx-auto opacity-50" /><p className="mb-0">No watermark uploaded</p></div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* ========================================= */}
        {/* TEAM MEMBERS SECTION (PARTNER/SUPPORT ONLY) */}
        {/* ========================================= */}
        {(isPartner || isSupport) && (
          <Row className="g-4 m-0 mt-2 px-2">
            <Col md={12}>
              <Card className="rounded-4 shadow-sm border-0">
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
                      <FaUsers /> Team Members
                      <Badge bg={hasReachedLimit ? "danger" : "secondary"} pill>
                        {currentMembersCount}/5
                      </Badge>
                    </h5>
                    <Button 
                      variant="primary" 
                      size="sm" 
                      onClick={() => setShowAddMemberModal(true)}
                      disabled={hasReachedLimit}
                    >
                      <FaUserPlus className="me-2" /> Add Member
                    </Button>
                  </div>
                  
                  {hasReachedLimit && (
                    <Alert variant="warning" className="py-2">
                      You have reached the maximum limit of 5 team members. To add a new member, please remove an existing one first.
                    </Alert>
                  )}

                  {currentMembersCount > 0 ? (
                    <Table responsive hover className="align-middle border mt-3">
                      <thead className="table-light">
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partnerData.team_members.map(member => (
                          <tr key={member.member_id}>
                            <td>{member.member_name}</td>
                            <td>{member.email_id}</td>
                            <td><Badge bg="info">{member.role.replace('_', ' ')}</Badge></td>
                            <td>
                              <Badge bg={member.status === 'A' ? 'success' : 'danger'}>
                                {member.status === 'A' ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="text-end">
                              <Button variant="outline-danger" size="sm" onClick={() => handleDeleteMember(member.member_id)}>
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center py-4 bg-light rounded text-muted mt-3">
                      <FaUsers size={32} className="mb-2 opacity-50" />
                      <p className="mb-0">No team members added yet.</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* ========================================= */}
        {/* DISCLAIMER SECTION                        */}
        {/* ========================================= */}
        <Row className="g-4 m-0 px-2 mt-0">
           <Col md={12}>
              <PartnerDisclaimer targetAdminId={targetAdminId || partnerData?.admin_id} />
           </Col>
        </Row>
      </div>

      {/* ========================================= */}
      {/* ADD MEMBER MODAL                          */}
      {/* ========================================= */}
      <Modal show={showAddMemberModal} onHide={() => {
        setShowAddMemberModal(false);
        setMemberMessage({ type: '', text: '' });
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold"><FaUserPlus className="me-2" />Add Team Member</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddMember}>
          <Modal.Body>
            {memberMessage.text && (
              <Alert variant={memberMessage.type} className="mb-3">
                {memberMessage.text}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label className="text-secondary fw-semibold">Member Name</Form.Label>
              <Form.Control
                type="text"
                value={newMemberData.member_name}
                onChange={(e) => setNewMemberData({ ...newMemberData, member_name: e.target.value })}
                required
                placeholder="Enter full name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="text-secondary fw-semibold">Email Address</Form.Label>
              <Form.Control
                type="email"
                value={newMemberData.email_id}
                onChange={(e) => setNewMemberData({ ...newMemberData, email_id: e.target.value })}
                required
                placeholder="member@company.com"
              />
              <Form.Text className="text-muted">They will use this email to log in.</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="text-secondary fw-semibold">Temporary Password</Form.Label>
              <Form.Control
                type="password"
                value={newMemberData.password}
                onChange={(e) => setNewMemberData({ ...newMemberData, password: e.target.value })}
                required
                placeholder="Create a strong password"
                minLength="6"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button variant="secondary" onClick={() => setShowAddMemberModal(false)} disabled={addingMember}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={addingMember}>
              {addingMember ? <><Spinner size="sm" className="me-2" />Adding...</> : 'Save Member'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </Wrapper>
  );
}

export default PartnerProfile;